import { useCallback, useEffect, useMemo, useState } from 'react';
import AutoAwesomeOutlinedIcon from '@mui/icons-material/AutoAwesomeOutlined';
import FactCheckOutlinedIcon from '@mui/icons-material/FactCheckOutlined';
import RefreshOutlinedIcon from '@mui/icons-material/RefreshOutlined';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Divider,
  Skeleton,
  Stack,
  Typography,
} from '@mui/material';

import type { ClaimCase, ClaimCaseListItem, ClaimCaseReviewDetail } from '../../../store';
import {
  useClaimCaseDetailQuery,
  useClaimCasesQuery,
  useGenerateClaimCaseReviewMutation,
} from '../../../store';
import { formatCents, SegmentControl } from '../shared';

import { ClaimCaseActions } from './ClaimCaseActions';
import { ClaimsReviewQueueRow, type ClaimsReviewQueueRowClaim } from './ClaimsReviewQueueRow';

type ClaimCaseSegment =
  | 'all'
  | 'rejected'
  | 'denied'
  | 'partial'
  | 'patientResponsibility'
  | 'secondary'
  | 'reversal'
  | 'unmatched'
  | 'manualReview';

const panelSx = {
  border: '1px solid',
  borderColor: 'divider',
  borderRadius: 1,
  bgcolor: '#fff',
  p: 2,
};

export function ClaimReviewWorkspace() {
  const [segment, setSegment] = useState<ClaimCaseSegment>('all');
  const [selectedClaimId, setSelectedClaimId] = useState<string | null>(null);

  const { data, isLoading, isFetching } = useClaimCasesQuery({
    segment,
    page: 1,
    perPage: 25,
  });

  const items = useMemo(() => data?.items ?? [], [data?.items]);

  useEffect(() => {
    if (!selectedClaimId && items[0]?.claim_id) {
      setSelectedClaimId(items[0].claim_id);
      return;
    }
    if (selectedClaimId && items.length > 0 && !items.some(item => item.claim_id === selectedClaimId)) {
      setSelectedClaimId(items[0].claim_id);
    }
  }, [items, selectedClaimId]);

  const selected = items.find(item => item.claim_id === selectedClaimId) ?? items[0] ?? null;

  // After a dismiss/snooze, optimistically advance to the adjacent claim (next,
  // else previous) so the cursor stays put rather than jumping to the top. The
  // cache invalidation refetches the list, and the effect above reconciles
  // selection once the dispositioned claim drops off.
  const handleDispositioned = useCallback(
    (dispositionedId: string) => {
      const idx = items.findIndex(item => item.claim_id === dispositionedId);
      const next = items[idx + 1] ?? items[idx - 1] ?? null;
      setSelectedClaimId(next?.claim_id ?? null);
    },
    [items],
  );

  const { data: detailData, isFetching: isDetailFetching } = useClaimCaseDetailQuery(
    { claimId: selected?.claim_id ?? '' },
    { skip: !selected?.claim_id },
  );

  if (isLoading && !data) {
    return (
      <Stack spacing={2}>
        <Skeleton variant="rounded" height={56} />
        <Skeleton variant="rounded" height={420} />
      </Stack>
    );
  }

  return (
    <Stack spacing={2}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2 }}>
        <SegmentControl
          segments={data?.segments ?? []}
          active={segment}
          onChange={key => setSegment(key as ClaimCaseSegment)}
        />
        <Typography sx={{ fontSize: 12, color: 'text.secondary', flexShrink: 0 }}>
          {data?.summary ?? ''}
        </Typography>
      </Box>

      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', lg: '320px minmax(0, 1fr)' },
          gap: 2,
          alignItems: 'start',
        }}
      >
        <Stack
          spacing={1}
          sx={{
            ...panelSx,
            maxHeight: { lg: 'calc(100vh - 260px)' },
            minHeight: { lg: 420 },
            overflow: 'hidden',
          }}
        >
          <Stack
            direction="row"
            spacing={1}
            sx={{ alignItems: 'center', justifyContent: 'space-between' }}
          >
            <Box>
              <Typography sx={{ fontSize: 12, fontWeight: 800, color: 'text.secondary' }}>
                Review queue
              </Typography>
              <Typography sx={{ mt: 0.25, fontSize: 12, color: 'text.secondary' }}>
                {isFetching ? 'Refreshing...' : `${data?.total ?? 0} claims`}
              </Typography>
            </Box>
            {isFetching && <CircularProgress size={16} />}
          </Stack>

          <Stack spacing={1} sx={{ minHeight: 0, overflowY: 'auto', pr: 0.5 }}>
            {items.length === 0 ? (
              <Alert severity="success" sx={{ fontSize: 12.5 }}>
                No problem claims in this segment.
              </Alert>
            ) : (
              items.map(item => (
                <ClaimsReviewQueueRow
                  key={item.claim_id}
                  claim={toQueueRow(item)}
                  active={item.claim_id === selected?.claim_id}
                  onClick={() => setSelectedClaimId(item.claim_id)}
                />
              ))
            )}
          </Stack>
        </Stack>

        <ClaimCaseDetailPanel
          claim={detailData?.claim_case ?? selected}
          review={detailData?.latest_review}
          isFetching={isDetailFetching}
          onDispositioned={handleDispositioned}
        />
      </Box>
    </Stack>
  );
}

function ClaimCaseDetailPanel({
  claim,
  review,
  isFetching,
  onDispositioned,
}: {
  claim: ClaimCaseListItem | ClaimCase | null;
  review?: ClaimCaseReviewDetail | null;
  isFetching: boolean;
  onDispositioned?: (claimId: string) => void;
}) {
  const [generateReview, { isLoading: isGenerating }] = useGenerateClaimCaseReviewMutation();
  const result = review?.structured_output;
  const primaryAction = result?.recommendation.primary_action;
  const patientBilling = result?.patient_billing;
  const amountAtRisk =
    result?.problem.amount_at_risk_cents ??
    result?.queue_summary.amount_at_risk_cents ??
    claimAmountAtRisk(claim) ??
    null;

  const summary = useMemo(() => {
    if (result?.problem.what_happened) return result.problem.what_happened;
    if (review?.resolution_path_explanation) return review.resolution_path_explanation;
    return claim?.brief ?? 'This claim does not have a generated review yet.';
  }, [claim?.brief, result, review?.resolution_path_explanation]);

  if (!claim) {
    return (
      <Box sx={panelSx}>
        <Typography color="text.secondary">Select a claim to review.</Typography>
      </Box>
    );
  }

  const claimId = claim.claim_id;
  const hasReview = Boolean(review?.structured_output || review?.claim_case_review_id);

  return (
    <Stack spacing={2}>
      <Box sx={panelSx}>
        <Stack spacing={1.25}>
          <Stack
            direction={{ xs: 'column', md: 'row' }}
            spacing={1.5}
            sx={{ justifyContent: 'space-between' }}
          >
            <Box sx={{ minWidth: 0 }}>
              <Typography sx={{ fontSize: 24, fontWeight: 700, lineHeight: 1.15 }}>
                {claim.patient_display} · {claim.date_of_service_start} ·{' '}
                {claim.primary_billing_code?.display ?? 'Billing code pending'}
              </Typography>
              <Typography sx={{ mt: 0.5, fontSize: 13, color: 'text.secondary' }}>
                {claimStageLabel(claim)}
                {(review?.root_cause_label || claimRootCauseLabel(claim)) &&
                  ` · ${review?.root_cause_label ?? claimRootCauseLabel(claim)}`}
                {' · '}
                {claim.payer_name} · {claim.provider_display}
              </Typography>
            </Box>
            <Stack
              direction="row"
              spacing={0.5}
              sx={{ flexShrink: 0, alignItems: 'center' }}
            >
              {amountAtRisk !== null && (
                <Typography
                  sx={{
                    fontSize: 13,
                    fontWeight: 760,
                    color: 'text.secondary',
                  }}
                >
                  {formatCents(amountAtRisk)} at risk
                </Typography>
              )}
              <ClaimCaseActions
                claimId={claimId}
                onDispositioned={() => onDispositioned?.(claimId)}
              />
            </Stack>
          </Stack>

          {!hasReview && (
            <Alert
              severity="info"
              action={
                <Button
                  size="small"
                  startIcon={<AutoAwesomeOutlinedIcon />}
                  disabled={isGenerating}
                  onClick={() => generateReview({ claimId })}
                >
                  Generate review
                </Button>
              }
            >
              This claim has lifecycle data but no generated review yet.
            </Alert>
          )}
        </Stack>
      </Box>

      <Box sx={panelSx}>
        <Stack spacing={1.5}>
          <Stack direction="row" spacing={1} sx={{ alignItems: 'center' }}>
            <AutoAwesomeOutlinedIcon sx={{ fontSize: 19, color: 'primary.main' }} />
            <Typography sx={{ fontSize: 12, fontWeight: 800, color: 'text.secondary' }}>
              Resolution brief
            </Typography>
            {isFetching && <CircularProgress size={14} />}
          </Stack>
          <Typography sx={{ fontSize: 20, fontWeight: 700, lineHeight: 1.2 }}>
            {result?.problem.headline ?? review?.primary_action_label ?? claim.brief}
          </Typography>
          <Typography sx={{ fontSize: 13.5, color: 'text.secondary', lineHeight: 1.55 }}>
            {summary}
          </Typography>
          {result?.resolution_path_explanation && (
            <Box sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1, p: 1.5 }}>
              <Typography sx={{ fontSize: 12, fontWeight: 800, color: 'text.secondary', mb: 0.5 }}>
                Suggested path
              </Typography>
              <Typography sx={{ fontSize: 13, color: 'text.secondary', lineHeight: 1.55 }}>
                {result.resolution_path_explanation}
              </Typography>
            </Box>
          )}
          {(primaryAction?.label || review?.primary_action_label) && (
            <Box sx={{ borderTop: '1px solid', borderColor: 'divider', pt: 1.25 }}>
              <Typography sx={{ fontSize: 12, fontWeight: 800, color: 'text.secondary' }}>
                Recommended next step
              </Typography>
              <Typography sx={{ mt: 0.4, fontSize: 15, fontWeight: 760, lineHeight: 1.35 }}>
                {primaryAction?.label ?? review?.primary_action_label}
              </Typography>
              {primaryAction?.next_step_instruction && (
                <Typography sx={{ mt: 0.4, fontSize: 13, color: 'text.secondary', lineHeight: 1.5 }}>
                  {primaryAction.next_step_instruction}
                </Typography>
              )}
            </Box>
          )}
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
            <Button
              variant="outlined"
              startIcon={<RefreshOutlinedIcon />}
              disabled={isGenerating}
              onClick={() => generateReview({ claimId, force: true })}
            >
              Regenerate
            </Button>
          </Stack>
        </Stack>
      </Box>

      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', md: 'minmax(0, 1fr) 320px' },
          gap: 2,
        }}
      >
        <Box sx={panelSx}>
          <Stack spacing={1.2}>
            <Stack direction="row" spacing={1} sx={{ alignItems: 'center' }}>
              <FactCheckOutlinedIcon sx={{ fontSize: 18, color: 'primary.main' }} />
              <Typography sx={{ fontSize: 12, fontWeight: 800, color: 'text.secondary' }}>
                Evidence and checks
              </Typography>
            </Stack>
            {(result?.verify_before_acting ?? []).slice(0, 4).map(item => (
              <EvidenceRow key={item.label} label={item.label} detail={item.reason} />
            ))}
            {(result?.do_not_do_yet ?? []).slice(0, 3).map(item => (
              <EvidenceRow key={item.label} label={item.label} detail={item.reason} warning />
            ))}
            {!result && (
              <Typography sx={{ fontSize: 13, color: 'text.secondary' }}>
                Generate a review to see source-backed evidence, guardrails, and verification
                steps.
              </Typography>
            )}
          </Stack>
        </Box>

        <Box sx={panelSx}>
          <Stack spacing={1.2}>
            <Typography sx={{ fontSize: 12, fontWeight: 800, color: 'text.secondary' }}>
              Balance guidance
            </Typography>
            <Typography sx={{ fontSize: 12.5, color: 'text.secondary', lineHeight: 1.45 }}>
              {patientBilling?.reason ??
                'Patient billing guidance will appear after claim case review finishes.'}
            </Typography>
            <Divider />
            <Typography sx={{ fontSize: 12.5, color: 'text.secondary', lineHeight: 1.45 }}>
              {result?.confidence.reason ?? review?.resolution_path_explanation ?? 'Confidence not available yet.'}
            </Typography>
          </Stack>
        </Box>
      </Box>
    </Stack>
  );
}

function EvidenceRow({
  label,
  detail,
  warning = false,
}: {
  label: string;
  detail: string;
  warning?: boolean;
}) {
  return (
    <Box sx={{ borderTop: '1px solid', borderColor: 'divider', pt: 1 }}>
      <Typography sx={{ fontSize: 13, fontWeight: 700, color: warning ? 'warning.dark' : 'text.primary' }}>
        {label}
      </Typography>
      <Typography sx={{ mt: 0.25, fontSize: 12.5, color: 'text.secondary', lineHeight: 1.45 }}>
        {detail}
      </Typography>
    </Box>
  );
}

function toQueueRow(item: ClaimCaseListItem): ClaimsReviewQueueRowClaim {
  return {
    id: item.claim_id,
    patient: item.patient_display,
    dos: item.date_of_service_start,
    payer: item.payer_name,
    amount: formatCents(item.amount_at_risk_cents),
    primaryBillingCode: item.primary_billing_code?.display ?? 'Code pending',
    stage: item.stage_label,
  };
}

function claimAmountAtRisk(claim: ClaimCaseListItem | ClaimCase | null | undefined) {
  if (!claim) return null;
  if ('financial_summary' in claim) return claim.financial_summary.amount_at_risk_cents;
  return claim.amount_at_risk_cents;
}

function claimStageLabel(claim: ClaimCaseListItem | ClaimCase) {
  if ('lifecycle' in claim) return claim.lifecycle.stage_label;
  return claim.stage_label;
}

function claimRootCauseLabel(claim: ClaimCaseListItem | ClaimCase) {
  if ('lifecycle' in claim) return claim.latest_review?.root_cause_label ?? null;
  return claim.root_cause_label ?? null;
}
